import React, { useState } from "react";
import { Search, Package, MapPin, Calendar, CheckCircle, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function Tracking() {
    const [guia, setGuia] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const search = async (e) => {
        e?.preventDefault?.();
        const g = guia.trim();
        if (!g) return;
        setLoading(true); setError(""); setData(null);
        try {
            const { data } = await api.get(`/c807/tracking/${encodeURIComponent(g)}`);
            setData(data);
            if (!data.found) setError("No se encontró información para esta guía");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Error al consultar el tracking");
        } finally { setLoading(false); }
    };

    return (
        <div className="max-w-3xl mx-auto px-6 py-16">
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--tb-border-gold)]/15 mb-4">
                    <Package className="w-8 h-8 text-[var(--tb-gold)]" />
                </div>
                <h1 className="font-display text-4xl mb-2">🇸🇻 Rastrea tu envío</h1>
                <p className="text-sm text-[var(--tb-text-2)]">Ingresa el número de guía C807 que recibiste por correo o WhatsApp.</p>
            </div>

            <form onSubmit={search} className="tb-card p-6 mb-8" data-testid="tracking-form">
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-3 text-[var(--tb-muted)] pointer-events-none" />
                        <input className="tb-input pl-9" placeholder="Ej. TERRA-23344-202606221" value={guia} onChange={(e) => setGuia(e.target.value.toUpperCase())} data-testid="tracking-input" />
                    </div>
                    <button type="submit" disabled={loading || !guia.trim()} className="tb-btn-primary disabled:opacity-50" data-testid="tracking-submit">
                        {loading ? "Buscando..." : "Rastrear"}
                    </button>
                </div>
            </form>

            {error && (
                <div className="tb-card p-6 text-center border-rose-500/40" data-testid="tracking-error">
                    <AlertCircle className="w-8 h-8 text-rose-400 mx-auto mb-2" />
                    <p className="text-sm">{error}</p>
                    <p className="text-xs text-[var(--tb-muted)] mt-1">Verifica el número o contacta soporte.</p>
                </div>
            )}

            {data?.found && (
                <div className="space-y-4" data-testid="tracking-results">
                    <div className="tb-card p-6">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="tb-overline">Guía</div>
                                <div className="font-mono text-lg text-[var(--tb-gold)]">{data.guia}</div>
                            </div>
                            {data.tipo_servicio && (
                                <span className="tb-badge tb-badge-muted">{data.tipo_servicio === "CCE" ? "Pago contra entrega" : "Entrega regular"}</span>
                            )}
                        </div>
                        {data.creada_en && (
                            <div className="text-xs text-[var(--tb-text-2)]">
                                Creada: {new Date(data.creada_en).toLocaleString("es-SV")}
                            </div>
                        )}
                    </div>

                    {data.events.length === 0 ? (
                        <div className="tb-card p-6 text-center text-sm text-[var(--tb-text-2)]" data-testid="tracking-no-events">
                            {data.message || "Aún no hay eventos del courier."}
                        </div>
                    ) : (
                        <div className="tb-card p-6">
                            <div className="tb-overline mb-4">Historial de eventos</div>
                            <ol className="space-y-4">
                                {data.events.slice().reverse().map((ev, i) => (
                                    <li key={i} className="flex gap-3" data-testid={`tracking-event-${i}`}>
                                        <div className="relative flex-shrink-0">
                                            <div className={`w-3 h-3 rounded-full ${i === 0 ? "bg-[var(--tb-gold)] ring-4 ring-[var(--tb-border-gold)]/30" : "bg-[var(--tb-muted)]"}`} />
                                            {i < data.events.length - 1 && <div className="absolute top-3 left-1/2 -translate-x-1/2 w-px h-full bg-[var(--tb-border)]" />}
                                        </div>
                                        <div className="flex-1 pb-3">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <CheckCircle className={`w-4 h-4 ${i === 0 ? "text-[var(--tb-gold)]" : "text-[var(--tb-muted)]"}`} />
                                                <span className="font-medium text-sm">{ev.estatus || ev.codigo || "Actualización"}</span>
                                                {ev.codigo && <span className="text-[10px] tb-badge tb-badge-muted">{ev.codigo}</span>}
                                            </div>
                                            {ev.fecha && (
                                                <div className="text-xs text-[var(--tb-text-2)] mt-1 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" /> {new Date(ev.fecha).toLocaleString("es-SV")}
                                                </div>
                                            )}
                                            {ev.observaciones && <div className="text-xs text-[var(--tb-text-2)] mt-1">{ev.observaciones}</div>}
                                            {(ev.latitud && ev.longitud) && (
                                                <a href={`https://maps.google.com/?q=${ev.latitud},${ev.longitud}`} target="_blank" rel="noreferrer" className="text-xs tb-link mt-1 inline-flex items-center gap-1">
                                                    <MapPin className="w-3 h-3" /> Ver en el mapa
                                                </a>
                                            )}
                                            {ev.pod && (
                                                <div className="mt-2">
                                                    <img src={ev.pod} alt="Prueba de entrega" className="max-w-xs rounded border border-[var(--tb-border)]" />
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
